import { ResultProps } from '@twiliointernal/services-common/components/searchResult/definitions/types';

import { SupportApi } from '@twiliointernal/e2-commons-shared-api';
import {
  Excerpt,
  FormatSearchResultsReturn,
  SearchResultsResponseData,
  RelevanceValue,
} from '@twiliointernal/e2-commons-shared-definitions';
import { logger, SegmentTypes } from '@twiliointernal/e2-commons-shared-libs';

import { constants } from './definitions';
import {
  getKnowledgeBaseUrlRelativePath,
  isKnowledgeBaseUrl,
} from './knowledgeBaseUrl';
import {
  KENDRA_ANSWER_KEY,
  MAX_SEARCH_RESULT_LENGTH,
  highLightElements,
  trackingDetails,
} from '../definitions/constants';

const formatTextWithHighlights = ({ text, highlights }: Excerpt): string => {
  const beginOffsets = highlights.map((highlight) => highlight.beginOffset);
  const endOffsets = highlights.map((highlight) => highlight.endOffset);

  const textCharacters = text.split('');
  const bodyWithHighLights = textCharacters.reduce(
    (accumulator, character, index) => {
      if (beginOffsets.includes(index)) {
        return `${accumulator}${highLightElements.start}${character}`;
      }

      if (endOffsets.includes(index)) {
        return `${accumulator}${character}${highLightElements.end}`;
      }

      return `${accumulator}${character}`;
    },
    '',
  );

  return bodyWithHighLights;
};

/**
 * This method takes in search results and formats them to match the
 * existing component API. The biggest formatting happens on the body
 * which needs to have emphasis added based on the users query.
 * For more details see the formatTextWithHighlights
 * @param results An array of search results
 * @returns A formatted array of search results
 */
const formatSearchResults = (
  searchValue: string,
  searchResultData: SearchResultsResponseData,
  trackEvent: SegmentTypes.SegmentTrackEventMethod,
): FormatSearchResultsReturn => {
  const { queryId, results } = searchResultData;
  let hasRecommendedResult = false;

  const trimmedSearchResults = [...results].slice(0, MAX_SEARCH_RESULT_LENGTH);

  const formattedResults = trimmedSearchResults.map(
    (
      { id, resultExcerpts, documentTitle, documentUri, scoreConfidence, type },
      index,
    ) => {
      // Formatted results joined on `\n` in case of multi-paragraph
      const body = resultExcerpts
        .map((result) => formatTextWithHighlights(result))
        .join('\n');

      // For the time being, we only care if the first result is recommended
      // If more than one result is marked as an 'ANSWER', we will only visually
      // distinguish the first one returned from Kendra
      const isRecommended =
        index === 0 &&
        !!resultExcerpts.find(
          (excerpt) => excerpt.key && excerpt.key === KENDRA_ANSWER_KEY,
        );

      // Track through Segment when recommended result is found
      // Only track once in the event there is more than one recommended result
      if (!hasRecommendedResult && isRecommended) {
        hasRecommendedResult = true;
        const eventName = trackingDetails.recommendedResultFound;

        logger.info(`${constants.logPrefixes.search}::[${eventName}]`, {
          searchValue,
        });
        trackEvent(eventName, {
          searchValue,
        });
      }

      const isExternal = !isKnowledgeBaseUrl(documentUri);
      const url = isExternal
        ? documentUri
        : getKnowledgeBaseUrlRelativePath(documentUri);

      const additionalTrackingValues = {
        searchValue,
        url,
        title: documentTitle.text,
      };

      const resultClickTrackingName = isRecommended
        ? trackingDetails.recommendedResultClicked
        : trackingDetails.selectDeflectionSuggestion(index);

      const userMethods: ResultProps['userMethods'] = {
        onFeedbackClick: async (relevanceValue) => {
          await SupportApi.sendUserFeedback({
            queryId,
            relevanceValue,
            resultId: id,
          });
        },
        onResultClick: async () => {
          await SupportApi.sendUserFeedback({ queryId, resultId: id });
        },
      };

      const trackingMethods: ResultProps['trackingMethods'] = {
        feedbackClick: (relevance: RelevanceValue) => {
          const eventName = trackingDetails.userFeedbackButton;

          logger.info(`${constants.logPrefixes.search}::[${eventName}]`, {
            relevance,
            searchValue,
          });
          trackEvent(eventName, {
            ...additionalTrackingValues,
            relevance,
          });
        },
        resultClick: () => {
          logger.info(
            `${constants.logPrefixes.search}::[${resultClickTrackingName}]`,
            { searchValue },
          );
          trackEvent(resultClickTrackingName, additionalTrackingValues);
        },
      };

      return {
        body,
        id,
        isExternal,
        isRecommended,
        queryId,
        scoreConfidence,
        title: documentTitle.text,
        trackingMethods,
        type,
        url,
        userMethods,
      };
    },
  );

  return { results: formattedResults, hasRecommendedResult };
};

export { formatSearchResults, formatTextWithHighlights };
